import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { checkEmptyInputValue } from '@pages/AdditionalServices/tabs/DeliveryForm/helpers';
import { LocalizedMessage } from '@services/requests/localizedMessagesService/interfaces';
import { UnitData } from '@services/requests/searchLoaders/unitLoader/UnitLoaderQuery';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PalletToEditState } from '../types';
import { EditPalletParametersDialogStyledContainer } from './StyledComponent';

interface EditPalletParametersDialogProps {
  palletToEdit: PalletToEditState;
  availablePalletUnits: UnitData[];
  onClose: () => void;
  onSave: (newSate: PalletToEditState) => void;
  onGenerateLocalizedName: (defaultName: string, localizations: LocalizedMessage[]) => string;
}

function EditPalletParametersDialog({
  palletToEdit,
  availablePalletUnits,
  onClose,
  onSave,
  onGenerateLocalizedName,
}: EditPalletParametersDialogProps) {
  const savedPalletRef = useRef<PalletToEditState>(null);
  const [palletState, setPalletState] = useState<PalletToEditState>(() => {
    return palletToEdit ?? null;
  });

  useEffect(() => {
    if (palletToEdit) {
      savedPalletRef.current = palletToEdit;
      setPalletState(palletToEdit);
    }
  }, [palletToEdit]);

  const { t } = useTranslation('Common');

  /**
   * Обработка изменения одного из значений параметров паллеты
   * @param property
   * @param value
   */
  const changePalletPropertyHandler = <T extends keyof PalletToEditState>(
    property: T,
    value: PalletToEditState[T]
  ) => {
    setPalletState((prevState) => (prevState ? { ...prevState, [property]: value } : prevState));
  };

  const palletToRender = palletState ?? savedPalletRef.current;

  const isQuantityInputInvalid = palletToRender
    ? checkEmptyInputValue(palletToRender.quantity)
    : false;
  const isWeightInputInvalid = palletToRender ? checkEmptyInputValue(palletToRender.weight) : false;

  const savePalletHandler = () => {
    if (isWeightInputInvalid || isQuantityInputInvalid) {
      return;
    }

    onSave(palletState);
    onClose();
  };

  return (
    <Dialog
      fullWidth
      open={!!palletToEdit}
      scroll={'body'}
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '800px',
          },
        },
      }}
    >
      <EditPalletParametersDialogStyledContainer>
        <Typography variant={'h2'}>
          {t('Common:EditingOfPalletTitle')} #{(palletToRender?.index ?? 0) + 1}
        </Typography>
        {palletToRender && (
          <Box className={'cargo-inputs-block'}>
            <Box width={'100%'}>
              <TextField
                fullWidth
                variant={'standard'}
                value={palletToRender.type}
                select
                sx={{
                  '& .MuiInput-input': {
                    color: '#383838!important',
                    fontStyle: 'normal!important',
                    fontWeight: '600!important',
                    lineHeight: '22px!important',
                    textAlign: 'right!important',
                    fontSize: '12px!important',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ margin: '0' }}>
                      <Typography
                        sx={{ color: 'grey[700]', position: 'absolute', pointerEvents: 'none' }}
                        component={'span'}
                        variant={'h5'}
                        className={'cargo-inputs-block__text-field'}
                      >
                        {t('Common:PalletParameters.PalletType')}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                onChange={(event) => changePalletPropertyHandler('type', event.target.value)}
              >
                {availablePalletUnits.map((option) => (
                  <MenuItem value={option.id} key={'pallet-' + option.id}>
                    {onGenerateLocalizedName(option.default_name, option.localized_names_data)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <TextField
              error={isQuantityInputInvalid}
              focused={isQuantityInputInvalid}
              fullWidth
              variant={'standard'}
              value={palletToRender.quantity}
              type={'number'}
              InputProps={{
                inputMode: 'numeric',
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      sx={{ color: 'grey[700]', position: 'absolute', pointerEvents: 'none' }}
                      component={'span'}
                      variant={'h5'}
                    >
                      {t('Common:PalletParameters.PalletCount')}
                    </Typography>
                  </InputAdornment>
                ),
              }}
              onFocus={() => changePalletPropertyHandler('quantity', parseInt(''))}
              onChange={(event) =>
                changePalletPropertyHandler('quantity', parseInt(event.target.value))
              }
            />

            <TextField
              error={isWeightInputInvalid}
              focused={isWeightInputInvalid}
              fullWidth
              variant={'standard'}
              value={palletToRender.weight}
              type={'number'}
              InputProps={{
                inputMode: 'numeric',
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      sx={{ color: 'grey[700]', position: 'absolute', pointerEvents: 'none' }}
                      component={'span'}
                      variant={'h5'}
                    >
                      {t('Common:PalletParameters.PalletWeight')}
                    </Typography>
                  </InputAdornment>
                ),
              }}
              onFocus={() => changePalletPropertyHandler('weight', parseInt(''))}
              onChange={(event) =>
                changePalletPropertyHandler('weight', parseInt(event.target.value))
              }
            />
          </Box>
        )}
        <div className="pallet-edit-control">
          <Button
            onClick={savePalletHandler}
            className={'pallet-edit-button'}
            disabled={isWeightInputInvalid || isQuantityInputInvalid}
            variant={'contained'}
            color={'primary'}
            fullWidth={false}
            size="medium"
          >
            {t('Common:PalletParameters.Save')}
          </Button>
          <Button
            onClick={onClose}
            className={'pallet-edit-button'}
            variant={'contained'}
            color={'error'}
            fullWidth={false}
            size="medium"
          >
            {t('Common:PalletParameters.Cancel')}
          </Button>
        </div>
      </EditPalletParametersDialogStyledContainer>
    </Dialog>
  );
}

export { EditPalletParametersDialog };
